import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { queryClient } from "../lib/queryClient";
import { meQuery, type Me } from "../hooks/useAuth";
import { hasPermission, useCan } from "../hooks/useCan";
import { BarYearPill } from "../components/bar/BarYearPill";
import { resolveBarRange } from "../components/bar/resolveBarRange";
import { BarTabs } from "../components/bar/BarTabs";
import { BarOverviewTab } from "../components/bar/tabs/BarOverviewTab";
import { BarItemsTable } from "../components/bar/BarItemsTable";
import { BarMatrix } from "../components/bar/BarMatrix";
import { BarChecksTable } from "../components/bar/BarChecksTable";
import { BarMenuTable } from "../components/bar/BarMenuTable";
import { BarUploadPanel } from "../components/bar/BarUploadPanel";

const searchSchema = z.object({
  tab: z.enum(["overview", "items", "matrix", "checks", "menu", "uploads"]).default("overview"),
  // Calendar year, defaulting to the current one, with an optional month inside
  // it — the same selector the tiers report uses.
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  granularity: z.enum(["day", "week", "month"]).optional(),
  group: z.string().optional(),
  category: z.string().optional(),
  q: z.string().optional(),
});

export const Route = createFileRoute("/_auth/bar")({
  staticData: {
    titleKey: "bar.title",
    breadcrumbKey: "breadcrumb.operate",
    breadcrumbN: 9,
  },
  validateSearch: searchSchema,
  beforeLoad: () => {
    const me = queryClient.getQueryData<Me>(meQuery.queryKey);
    if (!me) return;
    if (!hasPermission(me, "view_bar")) {
      throw redirect({ to: "/clients" });
    }
  },
  component: BarScreen,
});

function BarScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/bar" });
  const { tab, year, month, granularity, group, category, q } = Route.useSearch();
  const canEdit = useCan("manage_bar");

  const range = useMemo(
    () => resolveBarRange(year ?? new Date().getFullYear(), month, new Date()),
    [year, month],
  );

  // A year of daily bars is 365 columns of noise, and a single month of monthly
  // ones is a single column. Default to the grain the window can actually show;
  // the control still overrides it.
  const grain = granularity ?? (month == null ? "month" : "day");

  // Stable, because BarItemsTable's debounce effect depends on it. `replace`:
  // refining a search is one step, not a history entry per pause in typing.
  const onSearch = useCallback(
    (value: string) =>
      void navigate({
        to: "/bar",
        search: (prev) => ({ ...prev, q: value || undefined }),
        replace: true,
      }),
    [navigate],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{t("bar.title")}</h2>
          {/* Said once, on the page, rather than left for someone to discover:
              these numbers are the bar's own till and are not joined to members. */}
          <span style={{ fontSize: 12, color: "var(--fg-mute)" }}>{t("bar.subtitle")}</span>
        </div>
        {/* The uploads and menu tabs are about the whole mirror, not a window. */}
        {tab !== "uploads" && tab !== "menu" && <BarYearPill />}
      </div>

      <BarTabs />

      {tab === "overview" && <BarOverviewTab from={range.from} to={range.to} granularity={grain} />}
      {tab === "items" && (
        <BarItemsTable
          from={range.from}
          to={range.to}
          group={group ?? ""}
          category={category ?? ""}
          search={q ?? ""}
          canEdit={canEdit}
          onGroup={(value) =>
            void navigate({
              to: "/bar",
              search: (prev) => ({ ...prev, group: value || undefined }),
            })
          }
          onCategory={(value) =>
            void navigate({
              to: "/bar",
              search: (prev) => ({ ...prev, category: value || undefined }),
            })
          }
          onSearch={onSearch}
        />
      )}
      {tab === "matrix" && <BarMatrix from={range.from} to={range.to} granularity={grain} />}
      {tab === "checks" && <BarChecksTable from={range.from} to={range.to} />}
      {tab === "menu" && <BarMenuTable canEdit={canEdit} />}
      {tab === "uploads" && <BarUploadPanel canUpload={canEdit} />}
    </div>
  );
}
